import { useContext, useEffect, useRef } from "react";
import Tone from "../lib/tone";
import { SongContext } from "./Song";

type Props = {
  id: number;
  isPlaying: boolean;
  url: string;
  setPlayerAsLoaded: () => void;
  volume?: number;
  pan?: number;
  mute?: boolean;
  solo?: boolean;
  handleTrackFinished: (id: number, trackFinished: boolean) => void;
  setDuration: (duration: number) => void;
};

const Player: React.FC<Props> = ({
  id,
  isPlaying,
  url,
  setPlayerAsLoaded,
  volume = 0,
  pan = 0,
  mute,
  solo,
  handleTrackFinished,
  setDuration,
}) => {
  const { recorder, exporting } = useContext(SongContext);
  const audioBufferRef = useRef<any>(null);
  const playerRef = useRef<any>(null);
  const trackChannelBase = useRef(new Tone.Channel(volume, pan));

  useEffect(() => {
    if (playerRef.current === null) {
      audioBufferRef.current = new Tone.ToneAudioBuffer(url);
      playerRef.current = new Tone.Player(audioBufferRef.current, () => {
        setPlayerAsLoaded();
        setDuration(audioBufferRef.current.duration);
        playerRef.current.onstop = () => {
          console.log("Player stopped");
          handleTrackFinished(id, true);
        };
      });
    }
  }, [id, handleTrackFinished, setDuration, setPlayerAsLoaded, url]);

  useEffect(() => {
    if (isPlaying) {
      if (playerRef.current?.loaded) {
        playerRef.current.chain(trackChannelBase.current, Tone.Destination);
        playerRef.current.start();
      } else {
        alert("Audio not loaded yet");
      }
    } else {
      if (playerRef.current?.loaded) {
        playerRef.current.stop();
        playerRef.current.disconnect();
        trackChannelBase.current.disconnect();
      }
    }
  }, [isPlaying]);

  useEffect(() => {
    if (exporting) {
      if (playerRef.current?.loaded) {
        playerRef.current.chain(trackChannelBase.current, recorder.current);
        playerRef.current.start();
      } else {
        alert("Audio not loaded yet");
      }
    } else {
      if (playerRef.current?.loaded) {
        playerRef.current.stop();
        playerRef.current.disconnect();
        trackChannelBase.current.disconnect();
      }
    }
  }, [exporting, recorder]);

  useEffect(() => {
    trackChannelBase.current.volume.value = volume;
  }, [volume]);

  useEffect(() => {
    trackChannelBase.current.pan.value = pan;
  }, [pan]);

  useEffect(() => {
    trackChannelBase.current.mute = mute;
  }, [mute]);

  useEffect(() => {
    trackChannelBase.current.solo = solo;
  }, [solo]);

  return null;
};

export default Player;
